import type { CSSProperties, ReactNode } from 'react';
import { isExternal, useHref, useMedia, useRender } from '../context';
import { trackClick, type ClickKind } from '../tracking';

interface SmartLinkProps {
  href?: string | null;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
  tracking?: ClickKind | '' | null;
  trackingLocation?: string;
  newTab?: boolean;
  ariaLabel?: string;
}

/**
 * Link that resolves internal page references ("page:{id}") for the current language. When the target is not
 * published in this language it renders plain text rather than a broken link.
 */
export function SmartLink({ href, className, style, children, tracking, trackingLocation, newTab, ariaLabel }: SmartLinkProps) {
  const resolve = useHref();
  const { mode } = useRender();
  const url = resolve(href);
  if (!url) return <span className={className} style={style}>{children}</span>;
  const external = isExternal(url);
  const openNew = newTab ?? external;
  return (
    <a
      href={url}
      className={className}
      style={style}
      aria-label={ariaLabel}
      target={openNew ? '_blank' : undefined}
      rel={openNew ? 'noopener noreferrer' : undefined}
      onClick={(e) => {
        if (mode === 'editor') {
          e.preventDefault();
          return;
        }
        if (tracking) trackClick(tracking, trackingLocation ?? 'unknown', (e.currentTarget.innerText || '').trim(), url);
      }}
    >
      {children}
    </a>
  );
}

/** "Unsere <span>Dienstleistungen</span>" pattern used by all section titles of the theme. */
export function TitleHighlight({ title, highlight }: { title?: string; highlight?: string }) {
  return (
    <>
      {title}
      {title && highlight ? ' ' : null}
      {highlight ? <span>{highlight}</span> : null}
    </>
  );
}

interface ImgProps {
  mediaId?: string | null;
  className?: string;
  width?: number;
  height?: number;
  eager?: boolean;
  altOverride?: string;
  decorative?: boolean;
}

export function Img({ mediaId, className, width, height, eager, altOverride, decorative }: ImgProps) {
  const media = useMedia(mediaId);
  if (!media) return null;
  return (
    <img
      src={media.url}
      alt={decorative ? '' : (altOverride ?? media.alt ?? '')}
      className={className}
      width={width ?? media.width ?? undefined}
      height={height ?? media.height ?? undefined}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
    />
  );
}

/** Rich text is sanitized on the server when saved; the editor preview additionally runs DOMPurify. */
export function RichHtml({ html, className, as: Tag = 'div' }: { html?: string | null; className?: string; as?: 'div' | 'span' }) {
  const { sanitizeHtml } = useRender();
  if (!html) return null;
  const safe = sanitizeHtml ? sanitizeHtml(html) : html;
  return <Tag className={className} dangerouslySetInnerHTML={{ __html: safe }} />;
}

export function Stars({ rating, label }: { rating: number; label: string }) {
  const icons: ReactNode[] = [];
  for (let i = 1; i <= 5; i++) {
    const cls = rating >= i ? 'fa fa-star' : rating >= i - 0.5 ? 'fa fa-star-half-o' : 'fa fa-star-o';
    icons.push(<i key={i} className={cls} aria-hidden="true" />);
  }
  return (
    <div className="icon" role="img" aria-label={label}>
      {icons}
    </div>
  );
}
